/**
 * Sound, synthesised rather than loaded.
 *
 * Square and triangle waves through a short envelope: no audio files, no extra request, no
 * licensing question, and it is exactly the timbre the reference hardware had. Off by default is
 * tempting for politeness, but a silent game feels broken — so it starts on, never plays before
 * the first tap (browsers forbid it anyway), and the toggle is always one press away.
 */

const STORAGE_KEY = 'talas.sound.v1';

let context: AudioContext | null = null;
let enabled = read();

function read(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

function ensureContext(): AudioContext | null {
  if (!enabled) return null;
  if (typeof window === 'undefined') return null;
  if (!context) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    context = new Ctor();
  }
  if (context.state === 'suspended') void context.resume();
  return context;
}

interface Note {
  freq: number;
  at: number;
  ms: number;
  type?: OscillatorType;
  gain?: number;
}

function play(notes: Note[]): void {
  const ctx = ensureContext();
  if (!ctx) return;
  const now = ctx.currentTime;

  for (const note of notes) {
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = note.type ?? 'square';
    osc.frequency.setValueAtTime(note.freq, now + note.at / 1000);

    const start = now + note.at / 1000;
    const end = start + note.ms / 1000;
    const peak = (note.gain ?? 0.06) * 0.9;
    amp.gain.setValueAtTime(0.0001, start);
    amp.gain.exponentialRampToValueAtTime(peak, start + 0.008);
    amp.gain.exponentialRampToValueAtTime(0.0001, end);

    osc.connect(amp).connect(ctx.destination);
    osc.start(start);
    osc.stop(end + 0.02);
  }
}

export const sfx = {
  /** Cursor moving through a menu, or a step on the map. Deliberately quiet — it repeats a lot. */
  move: () => play([{ freq: 620, at: 0, ms: 45, gain: 0.03 }]),
  confirm: () => play([
    { freq: 740, at: 0, ms: 55 },
    { freq: 988, at: 45, ms: 80 },
  ]),
  cancel: () => play([{ freq: 320, at: 0, ms: 70, gain: 0.045 }]),
  bump: () => play([{ freq: 150, at: 0, ms: 60, type: 'triangle', gain: 0.05 }]),
  /** An island cleared, a revision posted. The only sound anyone should remember. */
  fanfare: () =>
    play([
      { freq: 659, at: 0, ms: 90 },
      { freq: 784, at: 85, ms: 90 },
      { freq: 988, at: 170, ms: 90 },
      { freq: 1319, at: 255, ms: 220 },
      { freq: 988, at: 255, ms: 220, type: 'triangle', gain: 0.035 },
    ]),
  error: () =>
    play([
      { freq: 300, at: 0, ms: 70, gain: 0.05 },
      { freq: 200, at: 65, ms: 110, gain: 0.05 },
    ]),
  /** One blip per few characters while the dialogue box types itself out. */
  blip: () => play([{ freq: 880 + Math.round(Math.random() * 60), at: 0, ms: 18, gain: 0.018 }]),

  /* ---------------------------------------------------------------- the duel */

  /**
   * A challenge arriving, and the arena opening. Two rising fourths and a held note — the shape a
   * handheld RPG uses to say something is about to happen, on the same two oscillators as the rest.
   */
  izazov: () =>
    play([
      { freq: 494, at: 0, ms: 70 },
      { freq: 659, at: 70, ms: 70 },
      { freq: 494, at: 150, ms: 70 },
      { freq: 659, at: 220, ms: 70 },
      { freq: 880, at: 300, ms: 260 },
      { freq: 440, at: 300, ms: 260, type: 'triangle', gain: 0.035 },
    ]),
  /** A vote landing as a move. Short and percussive; it can happen forty times in an evening. */
  udar: () =>
    play([
      { freq: 180, at: 0, ms: 45, type: 'triangle', gain: 0.05 },
      { freq: 90, at: 35, ms: 70, type: 'triangle', gain: 0.04 },
    ]),
  /** …and the same hit when it lands on the axis already doing the most damage. */
  udarJak: () =>
    play([
      { freq: 260, at: 0, ms: 40, type: 'triangle', gain: 0.055 },
      { freq: 150, at: 30, ms: 60, type: 'triangle', gain: 0.05 },
      { freq: 80, at: 80, ms: 110, type: 'triangle', gain: 0.045 },
    ]),
  /**
   * The low-health pulse — the most recognisable sound in the whole reference, and the most
   * anxious. Two beats and it stops, and it never repeats on a timer.
   */
  malo: () =>
    play([
      { freq: 1046, at: 0, ms: 60, gain: 0.028 },
      { freq: 1046, at: 190, ms: 60, gain: 0.028 },
    ]),
  pobeda: () =>
    play([
      { freq: 784, at: 0, ms: 80 },
      { freq: 784, at: 90, ms: 80 },
      { freq: 784, at: 180, ms: 80 },
      { freq: 1047, at: 270, ms: 320 },
      { freq: 659, at: 270, ms: 320, type: 'triangle', gain: 0.035 },
    ]),
  /** Not a punishment sound. It falls, it resolves, and it is over in half a second. */
  poraz: () =>
    play([
      { freq: 392, at: 0, ms: 110, type: 'triangle', gain: 0.045 },
      { freq: 330, at: 110, ms: 130, type: 'triangle', gain: 0.042 },
      { freq: 262, at: 240, ms: 260, type: 'triangle', gain: 0.038 },
    ]),
};

export function soundEnabled(): boolean {
  return enabled;
}

export function toggleSound(): boolean {
  enabled = !enabled;
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    /* private mode */
  }
  if (enabled) sfx.confirm();
  return enabled;
}
